import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatINR, formatWeight } from '@vj/shared';
import { api } from '../../lib/api.js';
import { TextField } from '../../components/ui/Field.jsx';
import { todayIso, currentFyStart } from './dateRange.js';

const MOVEMENT_LABEL = {
  purchase: 'Purchased (buy-back)',
  sale: 'Sold',
  issue: 'Issued out',
  receipt: 'Received back',
  repair_in: 'Received for repair',
  repair_out: 'Returned after repair',
  adjustment: 'Adjustment',
  melt: 'Melted',
};

const METAL_LABEL = { gold: 'Gold', silver: 'Silver', platinum: 'Platinum' };

function Balance({ b }) {
  return (
    <>
      <td className="tabular py-2 pr-3 text-right">{b.quantity}</td>
      <td className="tabular py-2 pr-3 text-right">{formatWeight(b.grossWt)}</td>
      <td className="tabular py-2 pr-3 text-right">{formatWeight(b.netWt)}</td>
      <td className="tabular py-2 pr-3 text-right">{formatWeight(b.fineWt)}</td>
      <td className="tabular py-2 text-right">{formatINR(b.value)}</td>
    </>
  );
}

function PurityRegister({ group }) {
  return (
    <section className="card p-5">
      <h2 className="mb-3 text-sm font-semibold">
        {METAL_LABEL[group.metal] ?? group.metal} — {group.label}
      </h2>
      <div className="table-scroll">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
              <th className="py-2 pr-3">Movement</th>
              <th className="py-2 pr-3 text-right">Qty</th>
              <th className="py-2 pr-3 text-right">Gross wt.</th>
              <th className="py-2 pr-3 text-right">Net wt.</th>
              <th className="py-2 pr-3 text-right">Fine wt.</th>
              <th className="py-2 text-right">Value</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-border">
              <td className="py-2 pr-3 font-medium">Opening balance</td>
              <Balance b={group.opening} />
            </tr>
            {group.movements.map((m) => (
              <tr key={m.type} className="border-b border-border last:border-0">
                <td className="py-2 pr-3 text-muted">
                  {MOVEMENT_LABEL[m.type] ?? m.type}
                  <span className="ml-1.5 text-xs text-muted">({m.count})</span>
                </td>
                <Balance b={m} />
              </tr>
            ))}
            <tr className="border-t-2 border-border font-semibold">
              <td className="py-2 pr-3">Closing balance</td>
              <Balance b={group.closing} />
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function StockRegisterPage() {
  const [from, setFrom] = useState(currentFyStart());
  const [to, setTo] = useState(todayIso());

  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'stock-register', from, to],
    queryFn: () => api.get('/reports/stock-register', { query: { from, to } }),
  });

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Stock register</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted">
          Opening balance, movements and closing balance by metal and purity — the record CGST Rule 56 expects a
          registered person to maintain. Derived from every stock-affecting sale, purchase, repair and issue/receipt
          on file; nothing here is editable.
        </p>
      </div>

      <div className="card flex flex-wrap items-end gap-3 p-4">
        <TextField label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <TextField label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        <p className="pb-2.5 text-xs text-muted">Defaults to the current financial year to date.</p>
      </div>

      {error ? (
        <p className="rounded-card border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          Could not load the stock register. Please try again.
        </p>
      ) : null}

      {isLoading ? null : !data?.purities?.length ? (
        <p className="rounded-card border border-border bg-surface px-4 py-3 text-sm text-muted">
          No stock movements recorded in this period.
        </p>
      ) : (
        data.purities.map((group) => <PurityRegister key={`${group.metal}-${group.purityId ?? 'none'}`} group={group} />)
      )}
    </div>
  );
}
